import { createMiddleware } from '@tanstack/react-start';
import { getHeader } from '@tanstack/react-start/server';
import jwt, { JwtPayload } from 'jsonwebtoken';
import jwksRsa from 'jwks-rsa';

import { parseCookies, UserInfo } from '@/lib/auth';
import { getUserFromOneRoster } from '@/lib/auth.oneroster';
import {
  COOKIE_NAMES,
  impersonatedUserIdCookieKey,
  UserRoles,
} from '@/lib/constants/auth.constants';
import { HttpStatus } from '@/lib/constants/http.status';
import { UnauthorizedError, ValidateTokenError } from '@/lib/errors';
import { OneRosterApiTyped } from '@/lib/http.server';

// JWKS client setup
const cognitoDomain = import.meta.env.VITE_ALPHA_COGNITO_AUTHORITY;
const jwksClient = jwksRsa({
  cache: true,
  rateLimit: true,
  jwksRequestsPerMinute: 10,
  jwksUri: `${cognitoDomain}/.well-known/jwks.json`,
});

// Function to get the public key from JWKS
async function getKey(header: jwt.JwtHeader): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!header.kid) {
      return reject(
        new ValidateTokenError(
          'Token header missing kid',
          HttpStatus.BadRequest
        )
      );
    }
    jwksClient.getSigningKey(header.kid, (err, key) => {
      if (err) {
        return reject(
          new ValidateTokenError(
            `Failed to get signing key: ${err.message}`,
            HttpStatus.InternalServerError,
            {
              kid: header.kid,
            }
          )
        );
      }
      const signingKey = key?.getPublicKey();
      if (!signingKey) {
        return reject(
          new ValidateTokenError(
            'Could not retrieve public key',
            HttpStatus.InternalServerError,
            {
              kid: header.kid,
            }
          )
        );
      }
      resolve(signingKey);
    });
  });
}

// Function to validate a Cognito JWT token (access or ID token)
async function validateToken(token: string): Promise<{
  valid: boolean;
  payload: JwtPayload | null;
  error?: ValidateTokenError;
}> {
  try {
    if (!token) {
      throw new ValidateTokenError('No token provided', HttpStatus.BadRequest, {
        token,
      });
    }
    if (!cognitoDomain) {
      throw new ValidateTokenError(
        'Cognito Authority URL not configured (VITE_ALPHA_COGNITO_AUTHORITY)',
        HttpStatus.InternalServerError
      );
    }

    const decodedJwt = jwt.decode(token, { complete: true });
    if (!decodedJwt || typeof decodedJwt !== 'object' || !decodedJwt.header) {
      throw new ValidateTokenError(
        'Invalid token format',
        HttpStatus.BadRequest,
        {
          token,
        }
      );
    }

    const publicKey = await getKey(decodedJwt.header);

    // Verify the token signature and expiration
    // Note: The issuer for Cognito access tokens is typically the user pool URL.
    const verifiedPayload = jwt.verify(token, publicKey, {
      // Check if the issuer matches the Cognito user pool URL (derived from the domain)
      // Example: https://cognito-idp.{region}.amazonaws.com/{userPoolId}
      // You might need to adjust this based on your exact Cognito setup if VITE_ALPHA_1EDTECH_API_IDP is just the domain.
      // For now, let's assume the domain includes the necessary path or adjust if errors occur.
      issuer: cognitoDomain, // Adjust if your VITE_ALPHA_1EDTECH_API_IDP is just the domain part
    }) as JwtPayload;

    return {
      valid: true,
      payload: verifiedPayload,
    };
  } catch (error) {
    let validationError: ValidateTokenError;
    if (error instanceof ValidateTokenError) {
      validationError = error;
    } else if (error instanceof Error) {
      const status = error.name === 'TokenExpiredError' ? 401 : 400;
      validationError = new ValidateTokenError(error.message, status);
    } else {
      validationError = new ValidateTokenError(
        'Unknown error during token validation',
        HttpStatus.InternalServerError
      );
    }
    console.error('Token validation failed:', validationError.message);
    return {
      valid: false,
      payload: null,
      error: validationError,
    };
  }
}

interface AuthContext {
  cognitoUser: UserInfo | null;
  accessTokenPayload: JwtPayload | null;
  authCookies: Record<string, string>;
}

export const userAuthMiddleware = createMiddleware({
  type: 'function',
}).server<AuthContext>(async ({ next }) => {
  const authCookiesHeader = getHeader('cookie');
  const cookies = parseCookies(authCookiesHeader || null);
  const accessToken = cookies[COOKIE_NAMES.ACCESS_TOKEN];
  const idToken = cookies[COOKIE_NAMES.ID_TOKEN];

  // Default context values
  let cognitoUser: UserInfo | null = null;
  let accessTokenPayload: JwtPayload | null = null;

  // Initialize authCookies, ensuring it's always a Record<string, string>
  const authCookies: Record<string, string> = cookies || {};
  const impersonation = {
    isImpersonated: authCookies[impersonatedUserIdCookieKey] !== undefined,
    impersonatedUserId: authCookies[impersonatedUserIdCookieKey],
  };

  if (!accessToken) {
    return await next({
      context: {
        cognitoUser,
        impersonation,
        accessTokenPayload,
        authCookies,
      } as AuthContext,
    });
  }

  // Validate the access token
  const validationResult = await validateToken(accessToken);

  if (validationResult.valid && validationResult.payload) {
    accessTokenPayload = validationResult.payload;

    // Validate and extract user info from the ID Token
    if (idToken) {
      const idTokenResult = await validateToken(idToken);
      if (idTokenResult.valid && idTokenResult.payload) {
        cognitoUser = {
          email: idTokenResult.payload.email as string,
          sub: idTokenResult.payload.sub as string,
          given_name: idTokenResult.payload.given_name as string,
          family_name: idTokenResult.payload.family_name as string,
          name: idTokenResult.payload.name as string,
        };
      } else {
        console.error(
          'ID token validation failed:',
          idTokenResult.error?.message || 'Unknown validation error'
        );
        cognitoUser = null;
      }
    } else {
      console.warn(
        'ID token cookie not found, cannot extract detailed user info.'
      );
    }
  } else {
    // Log the specific error if validation failed
    console.error(
      'Access token validation failed:',
      validationResult.error?.message || 'Unknown validation error'
    );
    // If validation fails (expired, invalid signature, etc.), treat as unauthenticated
    cognitoUser = null;
    accessTokenPayload = null;
  }

  return await next({
    context: {
      cognitoUser,
      impersonation,
      accessTokenPayload,
      authCookies,
    } as AuthContext,
  });
});

/**
 * Asserts that the user is authenticated.
 *
 * @param cognitoUser - The user info.
 * @throws UnauthorizedError if the user is not authenticated.
 */
export function assertUserAuthenticated(
  cognitoUser: UserInfo | null,
  shouldReport: boolean = false
): asserts cognitoUser is UserInfo {
  if (!cognitoUser) {
    throw new UnauthorizedError(
      'User not authenticated',
      undefined,
      shouldReport
    );
  }
}

/**
 * Asserts that the user has the given role.
 *
 * @param roles - The roles to check.
 * @param oneRosterApiTyped - The OneRoster API typed client.
 * @param cognitoUser - The Cognito user info.
 * @throws UnauthorizedError if the user does not have the given role.
 */
export async function assertUserHasRole(
  cognitoUser: UserInfo | null,
  oneRosterApiTyped: OneRosterApiTyped,
  roles: UserRoles[]
) {
  if (!cognitoUser) {
    throw new UnauthorizedError('User not authenticated', undefined, true);
  }

  const user = await getUserFromOneRoster(oneRosterApiTyped, cognitoUser.email);
  const userRoles = user?.roles?.map((role) => role.role) ?? [];

  if (userRoles.includes(UserRoles.SYSTEM_ADMINISTRATOR)) {
    return; // bypass validation for system admins
  }

  if (!userRoles.some((role) => roles.includes(role as UserRoles))) {
    throw new UnauthorizedError(
      'User does not have the required role',
      undefined,
      true
    );
  }
}

/**
 * Asserts that the user is either accessing their own data (self-access)
 * or has one of the allowed elevated roles.
 *
 * @param cognitoUser - The Cognito user info.
 * @param oneRosterApiTyped - The OneRoster API typed client.
 * @param targetUserId - The sourcedId of the user whose data is being accessed.
 * @param allowedRoles - Roles that are permitted to access other users' data.
 * @throws UnauthorizedError if the user is not authenticated, not the target user, and lacks the required role.
 */
export async function assertSelfOrRole(
  cognitoUser: UserInfo | null,
  oneRosterApiTyped: OneRosterApiTyped,
  targetUserId: string,
  allowedRoles: UserRoles[]
) {
  if (!cognitoUser) {
    throw new UnauthorizedError('User not authenticated', undefined, true);
  }

  const user = await getUserFromOneRoster(oneRosterApiTyped, cognitoUser.email);
  const userRoles = user?.roles?.map((role) => role.role) ?? [];

  // System admins always have access
  if (userRoles.includes(UserRoles.SYSTEM_ADMINISTRATOR)) {
    return;
  }

  // Self-access: the user is requesting their own data
  // Checks both OneRoster sourcedId and legacy MAP proctor student ID
  const legacyId = (user?.metadata as Record<string, unknown>)
    ?.legacyDashStudentId as string | undefined;
  if (user?.sourcedId === targetUserId || (legacyId && legacyId === targetUserId)) {
    return;
  }

  // Otherwise, must have an elevated role
  if (!userRoles.some((role) => allowedRoles.includes(role as UserRoles))) {
    throw new UnauthorizedError(
      'User does not have permission to access this resource',
      undefined,
      true
    );
  }
}
